import apiClient from './client';
import type { DpoRequest, DpoStats, ConsentRecord, DpoRequestStatus } from '../types';

// -------------------------------------------------------------------------
// Query params
// -------------------------------------------------------------------------

export interface SubjectRequestListParams {
  page?: number;
  size?: number;
  status?: DpoRequestStatus;
  sort?: string;
}

export interface ConsentListParams {
  page?: number;
  size?: number;
  employeeId?: string;
  sort?: string;
}

// -------------------------------------------------------------------------
// Backend response shapes (mapped from Java DTOs)
// -------------------------------------------------------------------------

/** Spring Page shape (after global camelCase→snake_case conversion) */
interface SpringPage<T> {
  content: T[];
  total_elements: number;
  total_pages: number;
  size: number;
  number: number;
}

/** Backend SubjectRequestDto shape (after snake_case conversion) */
interface SubjectRequestBackend {
  id: string;
  employee_id: string | null;
  employee_name: string | null;
  request_type: 'ACCESS' | 'ERASURE' | 'RECTIFICATION' | 'PORTABILITY';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED';
  notes: string | null;
  resolution: string | null;
  resolved_by: string | null;
  created_at: string;
  resolved_at: string | null;
}

/** Backend ConsentRecordDto shape (after snake_case conversion) */
interface ConsentRecordBackend {
  id: string;
  employee_id: string | null;
  employee_name: string | null;
  consent_type: 'CV_PROCESSING' | 'TENDER_MATCHING' | 'NOTIFICATION' | 'WITHDRAWAL';
  consent_given: boolean;
  consent_scope: string | null;
  channel: string | null;
  policy_version: string | null;
  recorded_at: string;
}

/** Backend DpoStatsDto shape (after snake_case conversion) */
interface DpoStatsBackend {
  open_requests: number;
  in_progress_requests: number;
  resolved_this_month: number;
  average_resolution_days: number;
  total_consents: number;
  active_consents: number;
  cvs_due_for_deletion: number;
}

export interface RetentionStats {
  retention_days: number;
  records_due_for_deletion: number;
  records_due_in30_days: number;
  total_employees: number;
  erased_employees: number;
}

export interface CreateSubjectRequestPayload {
  employeeId: string;
  requestType: 'ACCESS' | 'ERASURE' | 'RECTIFICATION' | 'PORTABILITY';
  notes?: string;
}

export interface ResolveSubjectRequestPayload {
  status: 'RESOLVED' | 'REJECTED';
  resolution?: string;
  resolvedBy?: string;
}

// -------------------------------------------------------------------------
// Mappers — backend DTO → frontend type
// -------------------------------------------------------------------------

const REQUEST_TYPE_MAP: Record<SubjectRequestBackend['request_type'], DpoRequest['request_type']> = {
  ACCESS: 'access',
  ERASURE: 'deletion',
  RECTIFICATION: 'rectification',
  PORTABILITY: 'portability',
};

const STATUS_MAP: Record<SubjectRequestBackend['status'], DpoRequestStatus> = {
  OPEN: 'pending',
  IN_PROGRESS: 'approved',
  RESOLVED: 'completed',
  REJECTED: 'rejected',
};

function mapSubjectRequest(sr: SubjectRequestBackend): DpoRequest {
  return {
    id: sr.id,
    request_type: REQUEST_TYPE_MAP[sr.request_type],
    subject_employee_id: sr.employee_id ?? '',
    subject_name: sr.employee_name ?? '(unknown)',
    requester_email: '',           // not available from backend yet
    description: sr.notes ?? '',
    status: STATUS_MAP[sr.status],
    created_at: sr.created_at,
    resolved_at: sr.resolved_at ?? undefined,
    resolution_notes: sr.resolution ?? undefined,
  };
}

function mapConsentRecord(c: ConsentRecordBackend): ConsentRecord {
  const typeLabel: Record<ConsentRecordBackend['consent_type'], string> = {
    CV_PROCESSING: 'CV Processing',
    TENDER_MATCHING: 'Tender Matching',
    NOTIFICATION: 'Notification',
    WITHDRAWAL: 'Withdrawal',
  };
  return {
    employee_id: c.employee_id ?? '',
    employee_name: c.employee_name ?? '(unknown)',
    consent_type: typeLabel[c.consent_type],
    consented: c.consent_given,
    consent_date: c.recorded_at ? c.recorded_at.split('T')[0] : undefined,
  };
}

function mapStats(s: DpoStatsBackend): DpoStats {
  return {
    total_requests: s.open_requests + s.in_progress_requests + s.resolved_this_month,
    pending_requests: s.open_requests + s.in_progress_requests,
    resolved_this_month: s.resolved_this_month,
    average_resolution_days: s.average_resolution_days,
    data_subjects_count: s.total_consents,
    anonymized_cvs_count: s.cvs_due_for_deletion,
  };
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

/**
 * Fetch aggregate DPO statistics for the dashboard header.
 */
export async function fetchDpoStats(): Promise<DpoStats> {
  const { data } = await apiClient.get<DpoStatsBackend>('/dpo/stats');
  return mapStats(data);
}

/**
 * Fetch a paginated list of data subject requests.
 */
export async function fetchSubjectRequests(
  params: SubjectRequestListParams = {}
): Promise<{ items: DpoRequest[]; total: number }> {
  const { data } = await apiClient.get<SpringPage<SubjectRequestBackend>>('/dpo/requests', {
    params: {
      page: params.page ?? 0,
      size: params.size ?? 50,
      ...(params.status !== undefined
        ? { status: STATUS_BACKEND_MAP[params.status] }
        : {}),
      sort: params.sort ?? 'createdAt,desc',
    },
  });
  return {
    items: data.content.map(mapSubjectRequest),
    total: data.total_elements,
  };
}

/**
 * Fetch a single data subject request by ID.
 */
export async function fetchSubjectRequest(id: string): Promise<DpoRequest> {
  const { data } = await apiClient.get<SubjectRequestBackend>(`/dpo/requests/${id}`);
  return mapSubjectRequest(data);
}

/**
 * Create a new data subject request.
 */
export async function createSubjectRequest(
  payload: CreateSubjectRequestPayload
): Promise<DpoRequest> {
  const { data } = await apiClient.post<SubjectRequestBackend>('/dpo/requests', payload);
  return mapSubjectRequest(data);
}

/**
 * Mark a subject request as resolved or rejected.
 */
export async function resolveSubjectRequest(
  id: string,
  payload: ResolveSubjectRequestPayload
): Promise<DpoRequest> {
  const { data } = await apiClient.put<SubjectRequestBackend>(
    `/dpo/requests/${id}/resolve`,
    payload
  );
  return mapSubjectRequest(data);
}

/**
 * Fetch a paginated list of consent records.
 */
export async function fetchConsents(
  params: ConsentListParams = {}
): Promise<{ items: ConsentRecord[]; total: number }> {
  const { data } = await apiClient.get<SpringPage<ConsentRecordBackend>>('/dpo/consents', {
    params: {
      page: params.page ?? 0,
      size: params.size ?? 50,
      ...(params.employeeId ? { employeeId: params.employeeId } : {}),
      sort: params.sort ?? 'recordedAt,desc',
    },
  });
  return {
    items: data.content.map(mapConsentRecord),
    total: data.total_elements,
  };
}

/**
 * Fetch a single consent record by ID.
 */
export async function fetchConsent(id: string): Promise<ConsentRecord> {
  const { data } = await apiClient.get<ConsentRecordBackend>(`/dpo/consents/${id}`);
  return mapConsentRecord(data);
}

/**
 * Fetch data retention statistics.
 */
export async function fetchRetentionStats(): Promise<RetentionStats> {
  const { data } = await apiClient.get<RetentionStats>('/dpo/retention/stats');
  return data;
}

// -------------------------------------------------------------------------
// Internal helper — map frontend status to backend enum
// -------------------------------------------------------------------------

const STATUS_BACKEND_MAP: Record<DpoRequestStatus, SubjectRequestBackend['status']> = {
  pending: 'OPEN',
  approved: 'IN_PROGRESS',
  completed: 'RESOLVED',
  rejected: 'REJECTED',
};
